// apps/client/src/components/leads/crmClassificationMap/CrmClassificationMapEditor.tsx
//
// Per-tenant editor for companies/{c}/crm_classification_maps/{provider}:
// which CRM value each lead classification answer becomes when VALUTTEN
// writes a lead back to the CRM. Mounted on the CRM Mappings page.
//
// Reads and writes go through leadCaptureAction, the same callable that owns
// the CRM stage overrides on that page:
//   getCrmClassificationMap  { companyId, provider }       -> { map, providers }
//   saveCrmClassificationMap { companyId, provider, map }
// The read path into the write-back adapters is workstream G's. An empty
// field means "not mapped": the CRM gets the classification only as the note
// line.
//
// Both actions are super_admin / administrator only on the server (reads
// included). The CRM Mappings page is also open to marketing, so the editor
// checks the signed-in role itself: any other role sees a one-line note and
// the action is never called.
//
// Spec: docs/plans/2026-10-23-WEBSITE-FORMS-PLATFORM.md §2.6 and §8.

import React, { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Save, Tags } from 'lucide-react';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc } from 'firebase/firestore';
import {
  CLASSIFICATION_DIMENSIONS,
  CLASSIFICATION_DIMENSION_LABELS,
  type ClassificationDimension,
  type ClassificationOption,
} from '@valutten/shared-schemas/dist/leadClassification';
import { db, functions } from '../../../lib/firebase';
import { useAuth } from '../../../context/AuthContext';
import { Skeleton } from '../../common/SkeletonLoader';
import { Spinner } from '../../common/Spinner';
import { callableErrorMessage } from '../../../hooks/useWebsiteConnections';
import {
  CRM_MAP_PROVIDER_LABELS,
  CRM_MAP_PROVIDER_VALUE_MAX,
  MYCRM_DEFAULT_PRODUCT_LINES,
  buildCrmMapForSave,
  collectClassificationValues,
  normaliseCrmMapProviders,
  sanitiseCrmMap,
  type CrmClassificationMap,
} from './crmClassificationMap';

interface Props {
  companyId: string | undefined;
  /**
   * The page's own write permission. Only super_admin / administrator can
   * read or save a CRM value map (leadCaptureAction get/saveCrmClassificationMap),
   * so the editor also requires one of those roles whatever this says.
   */
  canWrite: boolean;
  /**
   * Providers to offer until the server answers. Defaults to MyCRM, the one
   * implemented write-back adapter; the `providers` list in the
   * getCrmClassificationMap response replaces it once loaded.
   */
  providers?: string[];
}

type Edits = Record<ClassificationDimension, Record<string, string>>;

function editsFromMap(map: CrmClassificationMap): Edits {
  const out = {} as Edits;
  for (const dim of CLASSIFICATION_DIMENSIONS) out[dim] = { ...(map[dim] ?? {}) };
  return out;
}

const DEFAULT_PROVIDERS = ['mycrm'];

/** Roles leadCaptureAction lets read and save a CRM classification map. */
export const CRM_MAP_ROLES: readonly string[] = ['super_admin', 'administrator'];

export const CrmClassificationMapEditor: React.FC<Props> = ({ companyId, canWrite: pageCanWrite, providers: initialProviders = DEFAULT_PROVIDERS }) => {
  const { claims } = useAuth();
  const allowed = CRM_MAP_ROLES.includes(claims?.role ?? '');
  const canWrite = pageCanWrite && allowed;
  const [provider, setProvider] = useState<string>(initialProviders[0] ?? 'mycrm');
  const [serverProviders, setServerProviders] = useState<string[] | null>(null);
  const providers = serverProviders ?? initialProviders;
  const [edits, setEdits] = useState<Edits>(() => editsFromMap({}));
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [taxonomy, setTaxonomy] = useState<Record<string, Partial<Record<ClassificationDimension, ClassificationOption[]>>> | null>(null);

  // Classification values: defaults plus the tenant's configured options.
  useEffect(() => {
    if (!companyId || !allowed) return;
    let cancelled = false;
    getDoc(doc(db, `companies/${companyId}/lead_taxonomy/default`))
      .then((snap) => {
        if (cancelled || !snap.exists()) return;
        const c = (snap.data() as { classification?: unknown }).classification;
        if (c && typeof c === 'object') setTaxonomy(c as Record<string, Partial<Record<ClassificationDimension, ClassificationOption[]>>>);
      })
      .catch((err) => console.warn('[CrmClassificationMapEditor] lead_taxonomy read failed; using defaults', err));
    return () => {
      cancelled = true;
    };
  }, [companyId, allowed]);

  const values = useMemo(() => collectClassificationValues(taxonomy), [taxonomy]);

  // Load the stored map whenever the provider changes. Save stays disabled
  // until THIS provider has loaded: saving over a failed load would wipe the
  // tenant's map (the same guard CrmStageMappingsPage uses).
  useEffect(() => {
    if (!companyId || !allowed) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSaved(false);
    setLoadedFor(null);
    const fn = httpsCallable<
      { action: 'getCrmClassificationMap'; companyId: string; provider: string },
      { map?: unknown; providers?: unknown }
    >(functions, 'leadCaptureAction');
    fn({ action: 'getCrmClassificationMap', companyId, provider })
      .then((r) => {
        if (cancelled) return;
        setEdits(editsFromMap(sanitiseCrmMap(r.data?.map)));
        setLoadedFor(provider);
        const offered = normaliseCrmMapProviders(r.data?.providers);
        if (offered) {
          setServerProviders((prev) => (prev && prev.join('|') === offered.join('|') ? prev : offered));
          // The tenant has no map for the provider shown: move to one it has
          // (that load then runs for the new provider; Save waits for it).
          if (!offered.includes(provider)) setProvider(offered[0]);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setError(callableErrorMessage(err, 'The CRM value map could not be loaded. Refresh to try again.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, provider, allowed]);

  const setValue = (dim: ClassificationDimension, id: string, v: string) => {
    setSaved(false);
    setEdits((prev) => ({ ...prev, [dim]: { ...prev[dim], [id]: v } }));
  };

  const save = async () => {
    if (!companyId || loadedFor !== provider) return;
    const built = buildCrmMapForSave(edits);
    if (!built.ok) {
      setError(built.errors.join(' '));
      return;
    }
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const fn = httpsCallable<
        { action: 'saveCrmClassificationMap'; companyId: string; provider: string; map: CrmClassificationMap },
        unknown
      >(functions, 'leadCaptureAction');
      await fn({ action: 'saveCrmClassificationMap', companyId, provider, map: built.map });
      setSaved(true);
    } catch (err) {
      setError(callableErrorMessage(err, 'The CRM value map was not saved. Please try again.'));
    } finally {
      setSaving(false);
    }
  };

  const ready = loadedFor === provider;

  if (!allowed) {
    return (
      <p className="text-sm text-gray-400">
        Only admins can change which CRM values lead answers become.
      </p>
    );
  }

  return (
    <section aria-label="Classification to CRM values" className="rounded-lg border border-gray-700 bg-gray-900/40 p-5 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <h3 className="text-sm uppercase tracking-wider text-gray-400 font-semibold flex items-center gap-2">
            <Tags className="w-4 h-4" /> Lead answers to CRM values
          </h3>
          <p className="text-xs text-gray-500 mt-1">
            When a lead is sent to your CRM, each answer becomes the CRM value you enter here. Leave a field empty and the
            answer is only written into the lead note.
          </p>
        </div>
        {canWrite && (
          <button type="button" onClick={save} disabled={saving || loading || !ready}
            title={ready ? undefined : 'Waiting for the current map to load'}
            className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50 self-start">
            {saving ? <Spinner size="sm" /> : <Save className="w-4 h-4" />}
            Save values
          </button>
        )}
      </div>

      {providers.length > 1 && (
        <div className="flex gap-1 border-b border-gray-800" role="tablist">
          {providers.map((p) => (
            <button key={p} type="button" role="tab" aria-selected={p === provider} onClick={() => setProvider(p)}
              className={`px-4 py-2 text-sm font-medium rounded-t-md -mb-px border ${p === provider ? 'bg-blue-500/15 text-blue-300 border-blue-500/30 border-b-transparent' : 'text-gray-400 hover:text-white border-transparent'}`}>
              {CRM_MAP_PROVIDER_LABELS[p] ?? p}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div role="alert" className="rounded-md border border-red-500/30 bg-red-500/10 p-3 text-red-300 text-sm flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {saved && <p role="status" className="text-sm text-emerald-300">CRM values saved.</p>}

      {loading ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" aria-label="Loading CRM values">
          <Skeleton height="8rem" />
          <Skeleton height="8rem" />
        </div>
      ) : ready ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {CLASSIFICATION_DIMENSIONS.map((dim) => (
            <div key={dim} className="rounded-md border border-gray-700 p-3">
              <p className="text-xs font-semibold text-gray-300 mb-2">{CLASSIFICATION_DIMENSION_LABELS[dim]}</p>
              <div className="space-y-1.5">
                {values[dim].map((o) => {
                  const placeholder =
                    provider === 'mycrm' && dim === 'loanStructure' && MYCRM_DEFAULT_PRODUCT_LINES[o.id]
                      ? `${MYCRM_DEFAULT_PRODUCT_LINES[o.id]} (default)`
                      : 'Not mapped';
                  return (
                    <div key={o.id} className="grid grid-cols-2 gap-2 items-center">
                      <label htmlFor={`crm-map-${dim}-${o.id}`} className="text-xs text-gray-300 truncate">{o.label}</label>
                      <input id={`crm-map-${dim}-${o.id}`} type="text" maxLength={CRM_MAP_PROVIDER_VALUE_MAX}
                        value={edits[dim]?.[o.id] ?? ''} placeholder={placeholder} disabled={!canWrite}
                        onChange={(e) => setValue(dim, o.id, e.target.value)}
                        className="bg-gray-800 border border-gray-700 rounded-md px-2 py-1 text-xs disabled:opacity-60" />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
};

export default CrmClassificationMapEditor;
